export function n(value) {
  return Number(value) || 0;
}

export function money(value) {
  return "₹" + Math.round(n(value)).toLocaleString("en-IN");
}

export function pct(value) {
  return `${n(value).toFixed(1)}%`;
}

export function keyFromName(name) {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

export function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

export function annualIncomeComponent(row, annualMode) {
  const amount = n(row.annualIncome);
  if (annualMode === "spread") return amount / 12;
  if (annualMode === "selectedMonth") return amount;
  return 0;
}

export function customIncomeTotal(row, columns) {
  return columns.reduce((sum, col) => sum + n(row.customIncome?.[col.key]), 0);
}

export function incomePerStudent(row, columns, annualMode, increasePercent = 0) {
  const base =
    n(row.tuitionIncome) +
    n(row.admissionIncome) +
    n(row.developmentIncome) +
    n(row.transportIncome) +
    n(row.examIncome) +
    n(row.activityIncome) +
    n(row.otherIncome) +
    customIncomeTotal(row, columns) +
    annualIncomeComponent(row, annualMode);
  return base * (1 + n(increasePercent) / 100);
}

export function calcIncomeRow(row, columns, annualMode, increasePercent = 0, collectionBoost = 0) {
  const perStudent = incomePerStudent(row, columns, annualMode, increasePercent);
  const students = n(row.totalStudents);
  const ewsStudents = Math.min(n(row.ewsStudents), students);
  const remainingAfterEws = Math.max(students - ewsStudents, 0);
  const waiverStudents = Math.min(n(row.waiverStudents), remainingAfterEws);
  const fullPayingStudents = Math.max(students - ewsStudents - waiverStudents, 0);

  const gross = students * perStudent;
  const ewsBillable = ewsStudents * perStudent * (n(row.ewsPayPercent) / 100);
  const ewsConcession = ewsStudents * perStudent - ewsBillable;
  const waiverBillable =
    waiverStudents * perStudent * (1 - n(row.waiverPercent) / 100);
  const waiverConcession = waiverStudents * perStudent - waiverBillable;
  const fullBillable = fullPayingStudents * perStudent;
  const billed = fullBillable + ewsBillable + waiverBillable;

  const collectionPercent = Math.min(
    n(row.collectionPercent) + n(collectionBoost),
    100
  );
  const estimatedCollection = billed * (collectionPercent / 100);
  const collected =
    n(row.amountCollected) > 0 ? n(row.amountCollected) : estimatedCollection;
  const pending = n(row.pendingDue);
  const gap = Math.max(billed - collected - pending, 0);
  const concession = ewsConcession + waiverConcession;
  const avgCollected = students ? collected / students : 0;
  const avgBilled = students ? billed / students : 0;
  const rate = billed ? (collected / billed) * 100 : 0;

  return {
    gross,
    billed,
    collected,
    pending,
    gap,
    concession,
    ewsConcession,
    waiverConcession,
    avgCollected,
    avgBilled,
    rate,
    estimatedCollection,
  };
}

export function incomeTotals(rows, columns, annualMode, increasePercent = 0, collectionBoost = 0) {
  const result = {
    gross: 0,
    billed: 0,
    collected: 0,
    pending: 0,
    gap: 0,
    concession: 0,
    ewsConcession: 0,
    waiverConcession: 0,
    avgCollected: 0,
    avgBilled: 0,
    rate: 0,
    estimatedCollection: 0,
    students: 0,
  };

  rows.forEach((row) => {
    const calculated = calcIncomeRow(
      row,
      columns,
      annualMode,
      increasePercent,
      collectionBoost
    );
    Object.keys(calculated).forEach((key) => {
      result[key] = (result[key] || 0) + calculated[key];
    });
    result.students += n(row.totalStudents);
  });

  return result;
}

export function monthlyExpense(row) {
  const amount = n(row.amount);
  if (row.frequency === "monthly") return amount;
  if (row.frequency === "quarterly") return amount / 3;
  if (row.frequency === "annual") return amount / 12;
  if (row.frequency === "one-time") return amount / 12;
  return amount;
}

export function annualExpense(row) {
  return monthlyExpense(row) * 12;
}

export function expenseTotals(rows) {
  return rows.reduce(
    (acc, row) => {
      const monthly = monthlyExpense(row);
      acc.monthly += monthly;
      acc.annual += monthly * 12;
      acc.byCategory[row.category] =
        (acc.byCategory[row.category] || 0) + monthly;
      return acc;
    },
    { monthly: 0, annual: 0, byCategory: {} }
  );
}

export function displayMultiplier(viewMode) {
  return viewMode === "annual" ? 12 : 1;
}

// --- Export / Copy / CSV helpers ---

export function exportIncomeData(rows, columns, annualMode) {
  const headers = [
    "Class / Grade",
    "Section",
    "Total Students",
    "EWS Students",
    "EWS Pay %",
    "Waiver Students",
    "Waiver %",
    "Tuition Income",
    "Admission Income",
    "Development Income",
    "Transport Income",
    "Exam Income",
    "Activity Income",
    "Other Income",
    "Annual Income Heads",
    ...columns.map((col) => col.name),
    "Collection %",
    "Amount Collected",
    "Pending Due",
    "Income Billed",
    "Income Collected",
    "Average Income",
  ];

  const data = rows.map((row) => {
    const calculated = calcIncomeRow(row, columns, annualMode);
    return [
      row.className,
      row.section,
      row.totalStudents,
      row.ewsStudents,
      row.ewsPayPercent,
      row.waiverStudents,
      row.waiverPercent,
      row.tuitionIncome,
      row.admissionIncome,
      row.developmentIncome,
      row.transportIncome,
      row.examIncome,
      row.activityIncome,
      row.otherIncome,
      row.annualIncome,
      ...columns.map((col) => n(row.customIncome?.[col.key])),
      row.collectionPercent,
      row.amountCollected,
      row.pendingDue,
      Math.round(calculated.billed),
      Math.round(calculated.collected),
      Math.round(calculated.avgCollected),
    ];
  });

  return { headers, rows: data };
}

export function exportExpenseData(rows) {
  const headers = [
    "Category",
    "Expenditure Head",
    "What it includes",
    "Amount",
    "Frequency",
    "Monthly Equivalent",
    "Annual Equivalent",
    "Type",
    "Priority",
  ];

  const data = rows.map((row) => [
    row.category,
    row.head,
    row.includes,
    row.amount,
    row.frequency,
    Math.round(monthlyExpense(row)),
    Math.round(annualExpense(row)),
    row.type,
    row.priority,
  ]);

  return { headers, rows: data };
}

function escapeCSV(value) {
  const stringValue = String(value ?? "");
  return /[",\n\r]/.test(stringValue)
    ? `"${stringValue.replace(/"/g, '""')}"`
    : stringValue;
}

export function buildCSV(data) {
  return [data.headers, ...data.rows]
    .map((row) => row.map(escapeCSV).join(","))
    .join("\n");
}

export async function copyTableToClipboard(data) {
  const text = [data.headers, ...data.rows]
    .map((row) => row.join("\t"))
    .join("\n");
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
}

export function downloadCSV(csvText, filename) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
